import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiPlusCircle,
  FiUsers,
  FiTrendingUp,
  FiFileText,
  FiEdit, // using as calculator
  FiFile, // using as pdf
  FiMessageCircle,
  FiMail,
  FiBriefcase, // using as handshake
  FiCamera,
  FiArrowRight,
  FiMapPin,
  FiGrid, // using as bed
  FiMaximize, // using as square
  FiThumbsUp,
  FiMessageSquare,
  FiShare2,
  FiKey,
} from "react-icons/fi";
import "./MainDashboard.css";

const QUICK_LINKS = [
  { icon: <FiPlusCircle />, label: "Add Property", color: "text-blue-600", bg: "bg-blue-100" },
  { icon: <FiUsers />, label: "Manage Leads", color: "text-green-600", bg: "bg-green-100" },
  { icon: <FiTrendingUp />, label: "Analytics", color: "text-purple-600", bg: "bg-purple-100" },
  { icon: <FiFileText />, label: "Reports", color: "text-orange-600", bg: "bg-orange-100" },
];

const SERVICES_TOOLS = [
  { icon: <FiKey />, label: "Lease Management", path: "/customer/leases" },
  { icon: <FiEdit />, label: "EMI Calculator" },
  { icon: <FiFile />, label: "Brochure Maker" },
  { icon: <FiMessageCircle />, label: "Live Support" },
  { icon: <FiMail />, label: "Email Campaigns" },
  { icon: <FiBriefcase />, label: "Legal Services" },
  { icon: <FiCamera />, label: "Virtual Tour" },
];

const BannerCarousel = () => {
  const banners = [
    { img: "https://placehold.co/640x360/1d4ed8/ffffff?text=Special+Offer+1" },
    { img: "https://placehold.co/640x360/f97316/ffffff?text=Premium+Listings" },
    { img: "https://placehold.co/640x360/10b981/ffffff?text=Pro+Upgrade" },
    { img: "https://placehold.co/640x360/8b5cf6/ffffff?text=Webinar+Event" },
    { img: "https://placehold.co/640x360/334155/ffffff?text=Maintenance" },
  ];

  const carouselItems = [...banners, ...banners];

  return (
    <div className="banner-carousel-container">
      <div className="banner-track-wrapper">
        <div className="banner-track">
          {carouselItems.map((banner, idx) => (
            <div key={idx} className="banner-item">
              <img src={banner.img} alt={`Banner ${idx}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, onViewAll }) => (
  <div className="section-header">
    <h2>{title}</h2>
    <button onClick={onViewAll}>
      View All <FiArrowRight />
    </button>
  </div>
);

const PropertyCard = ({ item, assetUrl }) => {
  const imageSrc = item.propertyImages?.[0]?.image_url || `https://placehold.co/400x200/e2e8f0/64748b?text=Property`;
  return (
    <div className="property-card">
      <div className="property-img-wrapper">
        <img src={imageSrc} alt="Property" />
        <span className="property-tag-top">{item.purpose_name || "For Sale"}</span>
        <span className="property-tag-bottom">₹{item.budget || "N/A"}</span>
      </div>
      <div className="property-details">
        <h3 className="property-title">{item.title || "Property"}</h3>
        <p className="property-location">
          <FiMapPin /> {item.area_name || "N/A"}, {item.city_name || ""}
        </p>
        <div className="property-specs">
          <span>
            <FiGrid /> {item.type_name || "N/A"}
          </span>
          <span>
            <FiMaximize /> {item.sub_type_name || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
};

const PostCard = ({ item }) => (
  <div className="post-card">
    <div className="post-header">
      <div className="post-avatar">{item.customer_name?.charAt(0)?.toUpperCase() || "U"}</div>
      <div>
        <p className="post-author">{item.customer_name || "User"}</p>
        <p className="post-time">Recently</p>
      </div>
    </div>
    <div className="post-body">
      <p>
        {item.description || "Looking for property in this area..."}
      </p>
    </div>
    <div className="post-actions">
      <button className="post-action-btn">
        <FiThumbsUp /> {item.like_count || 0}
      </button>
      <button className="post-action-btn">
        <FiMessageSquare /> {item.comment_count || 0}
      </button>
      <button className="post-action-btn">
        <FiShare2 /> Share
      </button>
    </div>
  </div>
);

const PromoCard = ({ item, assetUrl }) => {
  const imageSrc = item.propertyImages?.[0]?.image_url || `https://placehold.co/400x200/cbd5e1/475569?text=Promo`;
  return (
    <div className="promo-card">
      <div className="promo-img-wrapper">
        <img src={imageSrc} alt="Promotion" />
        <span className="promo-tag-top">Featured</span>
      </div>
      <div className="promo-details">
        <h3 className="promo-title">{item.title || "Promotion"}</h3>
        <p className="promo-desc">{item.description?.substring(0, 80) || "Check out this great promotion!"}...</p>
        <div className="promo-action-wrapper">
          <button className="promo-action-btn">View Offer</button>
        </div>
      </div>
    </div>
  );
};

const MainDashboard = () => {
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState("");
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL;

  const [properties, setProperties] = useState([]);
  const [posts, setPosts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch Properties
        const propResponse = await axios.post(`${baseUrl}/property/list/preferred/v1`, {
          page_number: 1, page_size: 10, order_by: "DESC", keyword_search: ""
        }, { headers });
        setProperties(propResponse.data.propertyAssets || []);

        // Fetch Posts
        const postResponse = await axios.post(`${baseUrl}/post/list/preferred`, {
          service: "", state: "", city: "", keyword_search: "", page_number: 1, page_size: 10, order_by: "DESC"
        }, { headers });
        setPosts(postResponse.data.postAssets || []);

        // Fetch Promotions
        const userType = loggedInUser?.user?.userType?.name || loggedInUser?.userType;
        const isPartner = userType === "PARTNER";
        const promoEndpoint = isPartner ? `${baseUrl}/promotion/list/preferred` : `${baseUrl}/promotion/`;
        const promoPayload = isPartner
          ? { page_number: 1, page_size: 6, order_by: "DESC", is_partner: true }
          : { page_number: 1, page_size: 20, order_by: "DESC" };
        const promoResponse = await axios.post(promoEndpoint, promoPayload, { headers });
        setPromotions(
          promoResponse.data?.promotions ||
          promoResponse.data?.data ||
          promoResponse.data?.propertyAssets ||
          []
        );
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, baseUrl]);

  const renderHorizontalList = (Component, dataList) => (
    <div className="horizontal-list-container">
      {loading ? (
        <p className="loading-state">Loading data...</p>
      ) : dataList.length > 0 ? (
        dataList.map((item, idx) => (
          <Component key={idx} item={item} assetUrl={assetUrl} />
        ))
      ) : (
        <p className="empty-state">No data found</p>
      )}
    </div>
  );

  const userObj = loggedInUser?.user || loggedInUser;
  const rawFirstName = userObj?.first_name || userObj?.firstName;
  const rawLastName = userObj?.last_name || userObj?.lastName;
  const fullName = [rawFirstName, rawLastName].filter(Boolean).join(" ") || userObj?.name;
  const emailPrefix = userObj?.email_address ? userObj.email_address.split("@")[0] : null;
  const displayName = fullName || (emailPrefix ? emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1) : "User");

  return (
    <div className="main-dashboard">
      <div className="dashboard-greeting">
        <h1>
          {greeting}, {displayName}! <span role="img" aria-label="wave">👋</span>
        </h1>
        <p>Here is what's happening with your real estate business today.</p>
      </div>

      <BannerCarousel />

      <div className="dashboard-grid">
        <div className="dashboard-card col-span-1">
          <h2 className="dashboard-card-title">Quick Links</h2>
          <div className="quick-links-grid">
            {QUICK_LINKS.map((link, idx) => (
              <button key={idx} className="quick-link-btn">
                <div className={`quick-link-icon-wrapper ${link.bg} ${link.color}`}>
                  {link.icon}
                </div>
                <span className="quick-link-label">{link.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-card md-col-span-2">
          <SectionHeader title="All Services & Tools" onViewAll={() => alert("View All Services")} />
          <div className="services-grid">
            {SERVICES_TOOLS.map((tool, idx) => (
              <div
                key={idx}
                className="service-item"
                style={{ cursor: tool.path ? "pointer" : "default" }}
                onClick={() => tool.path && navigate(tool.path)}
              >
                <div className="service-icon-wrapper">
                  {tool.icon}
                </div>
                <span className="service-label">{tool.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="section-container">
        <SectionHeader title="Nearby Properties" onViewAll={() => alert("View All Properties")} />
        {renderHorizontalList(PropertyCard, properties)}
      </div>

      <div className="section-container">
        <SectionHeader title="Recent Posts" onViewAll={() => alert("View All Posts")} />
        {renderHorizontalList(PostCard, posts)}
      </div>

      <div className="section-container">
        <SectionHeader title="Active Promotions" onViewAll={() => alert("View All Promotions")} />
        {renderHorizontalList(PromoCard, promotions)}
      </div>
    </div>
  );
};

export default MainDashboard;
